import { FC, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface BackRouteButtonProps {
  className?: string;
  iconVariant?: "circle" | "square";
}

const formatRouteLabel = (path: string): string => {
  if (!path || path === "/") return "Home Page";
  const sanitized = path.split("?")[0].split("#")[0];
  const parts = sanitized.split("/").filter(Boolean);
  const last = parts[parts.length - 1];
  if (!last) return "Home Page";
  const text = last
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return `${text} Page`;
};

const BackRouteButton: FC<BackRouteButtonProps> = ({
  className = "",
  iconVariant = "circle",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState("/");

  useEffect(() => {
    const historyKey = "app_route_history";
    const currentPath = location.pathname + location.search;
    let history: string[] = [];

    try {
      const rawHistory = sessionStorage.getItem(historyKey);
      history = rawHistory ? (JSON.parse(rawHistory) as string[]) : [];
    } catch {
      history = [];
    }

    const previous = [...history]
      .reverse()
      .find((path) => path !== currentPath) ?? "";

    if (previous) {
      setPreviousPath(previous);
    } else if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        if (referrerUrl.origin === window.location.origin) {
          const referrerPath = referrerUrl.pathname + referrerUrl.search;
          setPreviousPath(referrerPath !== currentPath ? referrerPath : "/");
        } else {
          setPreviousPath("/");
        }
      } catch {
        setPreviousPath("/");
      }
    } else {
      setPreviousPath("/");
    }
  }, [location.pathname, location.search]);

  const previousRouteLabel = useMemo(
    () => formatRouteLabel(previousPath),
    [previousPath],
  );

  return (
    <div className={`inline-flex items-center gap-4 ${className}`}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="focus:outline-none"
        aria-label="Go back"
      >

          <svg width="38" height="34" viewBox="0 0 38 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="36" height="32" rx="6" fill="#121212" fillOpacity="0.2" />
            <rect x="0.5" y="0.5" width="37" height="33" rx="6.5" stroke="#121212" strokeOpacity="0.3" />
            <path d="M22 11C22 11 16 15.4189 16 17C16 18.5812 22 23 22 23" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>

      </button>

      <div className="leading-tight">
        <p className="text-2xl font-semibold text-text-color">Back</p>
        <p className="text-lg text-neutral-500">{previousRouteLabel}</p>
      </div>
    </div>
  );
};

export default BackRouteButton;
